import { Trans, useTranslation } from 'next-i18next'
import { Button, Heading, Panel, Span, Text } from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode } from 'react'
import Image from 'next/image'
import { NewTabLink } from '..'
import { useConsumerSizes } from '../../utils/responsive'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { useDisplayIllustrations } from '../../state/convergence/hooks'

const I18N_NAMESPACE = ['common', 'index']

interface TermsLinkProps {
  children?: ReactNode
}

const TermsLink: FunctionComponent<TermsLinkProps> = ({ children }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  return (
    <Span underline>
      <NewTabLink href={t('common:urls.consumerLendingUrl')}>{children}</NewTabLink>
    </Span>
  )
}

export const MonthlyPaymentsPanel: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const isDisplayIllustrations = useDisplayIllustrations()
  const { subheadingSize, buttonSize, textSize } = useConsumerSizes()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const onHandleLearnMore = (): void => {
    // @ts-ignore: OPERATION BLEED STOPPER
    logEvent(TrackingEvent.CLICKED_LEARN_MORE_MONTHLY_PAYMENTS, { outboundLink: t('common:urls:consumerLendingUrl') })
  }

  return (
    <Panel>
      <Panel.Content>
        <div className='lg:flex lg:gap-x-3' data-testid='monthly-payments-panel'>
          <div className='grid auto-cols-auto justify-between gap-x-3 lg:flex-1'>
            <div className='col-span-1 pb-3'>
              <Heading size={subheadingSize}>{t('index:monthlyPayments:title')}</Heading>
            </div>
            <div className='col-span-1 justify-self-end pb-3 lg:order-1 lg:justify-self-start'>
              <NewTabLink href={t('common:urls:payMonthlyUrl')}>
                <Button.Secondary padding='Fixed' size={buttonSize} onClick={onHandleLearnMore} aria-label={t('index:monthlyPayments:learnMore')}>
                  {t('index:monthlyPayments:learnMore')}
                </Button.Secondary>
              </NewTabLink>
            </div>
            <div className='col-span-2 pb-3'>
              <Text size={textSize}>{t('index:monthlyPayments:body')}</Text>
            </div>
            <div className='col-span-2 pb-6'>
              <Text color='Gray40' size='XXS'>
                <Trans
                  i18nKey={'index:monthlyPayments:finePrint'}
                  components={{
                    termsLink: <TermsLink />,
                  }}
                />
              </Text>
            </div>
          </div>
          {isDisplayIllustrations && (
            <div className='-mb-6 grid items-end lg:flex-1'>
              <Image
                width={479}
                height={215}
                src={`/images/treat-yourself.svg`}
                alt={t('index:welcome:existingUser:monthlyPayments:heading')}
                sizes='100vw'
                style={{
                  width: '100%',
                  height: 'auto',
                }}
              />
            </div>
          )}
        </div>
      </Panel.Content>
    </Panel>
  )
}
